import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Mutation, Query, Subscription } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { Materialize } from '@adornis/materialized-views/decorators/materialize.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { passwordRegexCheck } from '@adornis/users/db/validators.js';
import { changePasswordWithToken, changeUserPassword } from '@adornis/users/server/password.js';
import { generatePasswordChangeToken } from '@adornis/users/server/token.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import type { Bundesland, Gender } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import { concatMap, delay, from, map, of, switchMap } from 'rxjs';
import { Adresse } from '../../../adresse.js';
import { ChancenUserActivityAnalytics } from '../../../analytics/ChancenUserActivityAnalytics.js';
import { CRMFlags } from '../../../CRMFlags.js';
import { MailEventLog, MailEventLogType } from '../../../eventlogs/MailEventlog.js';
import { registerAtChancenCampus, registerPasswordSetAtChancenCampus } from '../../../mailer/mailTemplates.js';
import { chancenWerkMailer } from '../../../mailer/server/mailer.js';
import { type AllPermissions, ChancenPermission } from '../../../permissions.js';
import { Role } from '../../../semantics/Role.js';
import { SemanticRelationship } from '../../../semantics/SemanticRelationship.js';
import { chancenwerkUserMatSelectionSet } from './ChancenWerkUserMatSelectionSet.js';
import type { Student } from './Student.js';

@Materialize({ selectionSet: chancenwerkUserMatSelectionSet })
@Entity()
export class ChancenwerkUser extends AdornisUser<AllPermissions> {
  public static override _class = 'ChancenwerkUser';
  public static passwordChangePath = 'authentication/reset-password';

  public static readableName = 'Abstrakter ChancenWerk Nutzer. Nicht benutzen!';

  public static override get allFields() {
    return selectionSet(() => ChancenwerkUser, 8);
  }

  @Field(() => String)
  public createdBy: Maybe<string>;

  @DataLoadedField(() => ChancenwerkUser, ({ createdBy }) => createdBy)
  public createdByPerson: Maybe<ChancenwerkUser>;

  @Field(() => CRMFlags, { default: v => v ?? new CRMFlags({}) })
  public flags: Maybe<CRMFlags>;

  @validate<ChancenwerkUser>(({ value, key, source, step, target }) => {
    if (target._class === 'TemporaryUser') return;
    if (!step)
      if (!value) {
        throw new ValidationError('Bitte geben Sie einen Nutzernamen an.', {
          key,
          target,
          path: [],
          translationKey: 'errorUserUsername',
        });
      }
  })
  @Field(() => String)
  public override username!: string;

  @validate<ChancenwerkUser>(({ value, key, source, step, target }) => {
    if (target._class === 'ExternalContactPerson') return;
    if (target._class === 'VerwaltungsMitarbeiter') return;
    if (step === 2 || !step)
      if (!value) {
        throw new ValidationError('Bitte geben Sie einen Vornamen an.', {
          key,
          target,
          path: [],
          translationKey: 'errorUserVorname',
        });
      }
  })
  @Field(() => String)
  public vorname?: string;

  @validate<ChancenwerkUser>(({ value, key, source, step, target }) => {
    if (target._class === 'VerwaltungsMitarbeiter') return;
    if (step === 2 || !step)
      if (!value) {
        throw new ValidationError('Bitte geben Sie einen Nachnamen an.', {
          key,
          target,
          path: [],
          translationKey: 'errorUserNachname',
        });
      }
  })
  @Field(() => String)
  public nachname?: string;

  @Field(() => Adresse, { default: v => v ?? new Adresse({}) })
  public adresse: Maybe<Adresse>;

  @Field(() => String)
  public bundesland: Maybe<Bundesland>;

  @Field(() => DateTime)
  public lastPasswordChange: Maybe<DateTime>;

  @Field(() => DateTime)
  public lastActivity: Maybe<DateTime>;

  @Field(() => DateTime)
  public geburtstag: Maybe<DateTime>;

  @Field(() => String)
  public geschlecht: Maybe<Gender>;

  @Field(() => String)
  public salutationInConversation: Maybe<string>;

  @Field(() => String)
  public salutationInLetter: Maybe<string>;

  @Field(() => String)
  public title: Maybe<string>;

  @Field(() => String)
  public handynummer: Maybe<string>;

  @Field(() => String)
  public secondTelefone: Maybe<string>;

  @Field(() => String)
  public contactSource: Maybe<string>;

  @Field(() => ID)
  public contactSourcePersonID: Maybe<string>;

  @DataLoadedField(() => ChancenwerkUser, ({ contactSourcePersonID }) => contactSourcePersonID)
  public contactSourcePerson: Maybe<ChancenwerkUser>;

  @Field(() => ID)
  public personalSignOnChrstimasCardFromID: Maybe<string>;

  @Field(() => [ID])
  public personalSignOnChrstimasCardFromIDs: Maybe<string[]>;

  @Field(() => [SemanticRelationship], {
    resolve: function (this: ChancenwerkUser) {
      return async () => {
        const relations = await loadMongoData(SemanticRelationship, { subjectID: this._id });
        const activeRelations = relations
          .map(r => constructValue(r) as SemanticRelationship)
          .filter(r => (this._class === 'Student' ? (this as Student).active : r.isActive()));

        return activeRelations;
      };
    },
  })
  public activeRelations: Maybe<SemanticRelationship[]>;

  @Field(() => [SemanticRelationship], {
    resolve: function (this: ChancenwerkUser) {
      return async () => {
        const relations = await loadMongoData(SemanticRelationship, { subjectID: this._id });

        const inactiveRelations = relations
          .map(r => constructValue(r) as SemanticRelationship)
          .filter(r => (this._class === 'Student' ? !(this as Student).active : !r.isActive()));

        return inactiveRelations;
      };
    },
  })
  public inactiveRelations: Maybe<SemanticRelationship[]>;

  @DataLoadedField(
    () => [ChancenwerkUser],
    ({ personalSignOnChrstimasCardFromIDs }) => personalSignOnChrstimasCardFromIDs ?? [],
  )
  public personalSignOnChrstimasCardFrom: Maybe<ChancenwerkUser[]>;

  @DataLoadedField(() => ChancenUserActivityAnalytics, ({ _id }) => ({ userID: _id }))
  public activityAnalytics: Maybe<ChancenUserActivityAnalytics>;

  @Field(() => [String], { default: v => v ?? [] })
  public savedCampusSectionIDs: Maybe<string[]>;

  static override async beforeCreate(entity: ChancenwerkUser) {
    const user: string = !context.serverContext ? context.userID : '';
    entity.createdBy = user;

    return super.beforeCreate(entity);
  }

  /**
   * @deprecated Dont check on roles, use the permission guard with any context, and make a permission for your private content
   * @param campusRole The role to check for
   * @returns whether the user has the specific role
   */
  public checkRole(campusRole: Role) {
    if (this.roles.findIndex(role => role.name === campusRole) !== -1) return true;
    return false;
  }

  static get availableGrades() {
    return [...Array(13).keys()].map(x => x + 1);
  }

  public get userEmail() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return this.email?.includes('dummy') ? null : this.email;
  }

  public static async getPasswordChangeLink(userID: string, reroute: string, ttu: number = 1) {
    const resetLink = new URL(projectConfig.get('NETZ_URL') + this.passwordChangePath);
    resetLink.searchParams.append(
      'token',
      await generatePasswordChangeToken({
        userID,
        userClass: ChancenwerkUser._class,
        ttu,
        ttl: 1e3 * 60 * 60 * 24 * 14,
      }),
    );

    let link = '';

    // Build redirect link
    switch (reroute) {
      case 'chancencampus':
        link = projectConfig.get('CAMPUS_URL') + 'chancencampus/student/dashboard';
        break;
      case 'chancenraum':
        link = projectConfig.get('RAUM_URL');
        break;
      case 'chancenlogistik':
        link = projectConfig.get('LOGISTIK_URL');
        break;
      default:
        link = projectConfig.get('NETZ_URL');
        break;
    }

    resetLink.searchParams.append('reroute', link);
    return resetLink;
  }

  @Mutation(() => Boolean)
  public static changeUserPassword(
    @Arg('token', () => String) token: string,
    @Arg('password', () => String) password: string,
  ) {
    return async (gqlFields: void) => {
      if (!passwordRegexCheck(password)) {
        throw new Error('Bitte stell sicher, dass du alle Anforderungen erfüllts');
      }
      try {
        const user = await changePasswordWithToken(token, password);
        const coll = await getRawCollection<EntityData<ChancenwerkUser>>(this._collectionName);
        await coll.updateOne(
          { _id: user._id },
          { $set: { emailConfirmed: true, lastPasswordChange: DateTime.now().toJSDate() } },
        );
      } catch (e: any) {
        logger.error(e);
        throw new Error('Dieser Link wurde bereits genutzt.');
      }
      return true;
    };
  }

  static async canChangePasswordWithoutToken(userID: string) {
    const caller = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
      roles: { permissions: 1, contexts: 1, name: 1 },
    });
    return caller?.hasPermission(ChancenPermission.raum.CHANGE_PASSWORD);
  }

  static async afterPasswordChangeWithoutToken(userID: string) {}

  @Mutation(() => Boolean)
  public static changePasswordWithoutTokenAllowed(@Arg('userID', () => String) userID: string) {
    return async (gqlFields: void) => {
      return this.canChangePasswordWithoutToken(userID);
    };
  }

  @Mutation(() => Boolean)
  public static changeUserPasswordWithoutToken(
    @Arg('userID', () => String) userID: string,
    @Arg('password', () => String) password: string,
  ) {
    return async (gqlFields: void) => {
      if (!(await this.canChangePasswordWithoutToken(userID))) {
        throw new Error('keine Brechtigung das Passwort zu ändern');
      }
      if (!passwordRegexCheck(password)) {
        throw new Error('Bitte stell sicher, dass du alle Anforderungen erfüllts');
      }

      try {
        await changeUserPassword(userID, password);
        const coll = await getRawCollection<EntityData<ChancenwerkUser>>(this._collectionName);
        await coll.updateOne(
          { _id: userID },
          {
            $set: {
              emailConfirmed: true,
              lastPasswordChange: DateTime.now().toJSDate(),
              'joinInformation.campusRegisterDataSend': true,
            },
          },
        );
        await this.afterPasswordChangeWithoutToken(userID);
      } catch (e: any) {
        logger.error(e);
        throw new Error('Passwort änderung war nicht erfolgreich');
      }
      return true;
    };
  }
  protected async getComposedRegisterMail() {
    return Message.compose({
      subject: 'Willkommen auf dem ChancenCampus',
      html: registerAtChancenCampus({
        username: this.username,
        confirmationUrl: (await ChancenwerkUser.getPasswordChangeLink(this._id, 'chancencampus')).toString(),
      }),
    });
  }

  protected async getComposedPasswordSetRegisterMail() {
    return Message.compose({
      subject: 'Willkommen auf dem ChancenCampus',
      html: registerPasswordSetAtChancenCampus({
        username: this.username,
        confirmationUrl: (await ChancenwerkUser.getPasswordChangeLink(this._id, 'chancencampus')).toString(),
      }),
    });
  }

  protected async saveMailEventLog(error?: Error) {
    await new MailEventLog({
      userID: this._id,
      mailType: MailEventLogType.REGISTER,
      successful: !error,
      sendMailError: error?.message,
      emailAddress: this.email,
    }).create();
  }

  protected async sendRegistrationMail() {
    if (!A.isServer) {
      throw new Error('This is a server-side method only');
    }

    let mail = [this.email];

    // *
    // * If this is a student and the email is a dummy email, use the studentCreationMailOverride
    // *
    if (this._class === 'Student' && mail[0]?.includes('dummy-')) {
      const relationshipType = await import('../../../semantics/StudentParticipationRelationship.js');
      const kpf = await import('../../kpf/entities/KPF.js');

      const relationshipCol = await getRawCollection<{ objectID: string }>(
        relationshipType.StudentParticipationRelationship._collectionName,
      );
      const kpfCollection = await getRawCollection<{ _id: string; studentCreationMailOverride: string[] }>(
        kpf.KPF._collectionName,
      );

      const relations = (await relationshipCol.find({ subjectID: this._id }).toArray()) as Maybe<
        { objectID: string }[]
      >;

      const kpfs = (await kpfCollection
        .find({ _id: { $in: relations?.map(r => r.objectID as string) } })
        .toArray()) as Maybe<{ studentCreationMailOverride: string[] }[]>;

      const override = kpfs?.[0]?.studentCreationMailOverride;
      if (!override) {
        throw new Error('No studentCreationMailOverride set for this user');
      }
      mail = override;
    }

    await chancenWerkMailer.sendMail(await this.getComposedRegisterMail(), new Recipient(mail)).catch(async e => {
      await this.saveMailEventLog(e);
      throw e;
    });
    await this.saveMailEventLog();
  }

  @Mutation(() => Boolean)
  public static sendRegistrationMailForUserID(@Arg('userID', () => String) userID: string) {
    return async () => {
      const coll = await getRawCollection<ChancenwerkUser>(ChancenwerkUser._collectionName);
      const user = constructValue(await coll.findOne({ _id: userID })) as ChancenwerkUser;
      if (!user) throw new Error('Invalid userID given');

      try {
        await user.sendRegistrationMail();
      } catch (error) {
        logger.error(error);
        logger.error('Couldnt send Registermail to: ' + user.email + ' with the user id: ' + user._id);
        return false;
      }
      return true;
    };
  }

  @Mutation(() => Boolean)
  public static registerUserActivity() {
    return async () => {
      const caller = await CurrentUserInfo.getMyself<ChancenwerkUser>()({ _id: 1, lastActivity: 1 });
      if (!caller) {
        return false;
      }

      const userCol = await getRawCollection<EntityData<ChancenwerkUser>>(ChancenwerkUser._collectionName);
      await userCol.updateOne({ _id: caller._id }, { $set: { lastActivity: DateTime.now().toJSDate() } });
      return true;
    };
  }

  protected async sendPasswordSetRegistrationMail() {
    if (!A.isServer) {
      throw new Error('This is a server-side method only');
    }

    await chancenWerkMailer
      .sendMail(await this.getComposedPasswordSetRegisterMail(), new Recipient(this.email))
      .catch(async e => {
        await this.saveMailEventLog(e);
        throw e;
      });
    await this.saveMailEventLog();
  }

  /**
   * Sends a registration mail to the user with the given userID after their first password is set
   * @param userID The user to send the mail to
   * @returns Whether the mail was sent successfully
   */
  @Mutation(() => Boolean)
  public static sendPasswordSetRegistrationMailForUserID(@Arg('userID', () => String) userID: string) {
    return async () => {
      const coll = await getRawCollection<ChancenwerkUser>(ChancenwerkUser._collectionName);
      const user = constructValue(await coll.findOne({ _id: userID })) as ChancenwerkUser;
      if (!user) throw new Error('Invalid userID given');

      try {
        await user.sendPasswordSetRegistrationMail();
      } catch (error) {
        logger.error(error);
        logger.error('Couldnt send Registermail to: ' + user.email + ' with the user id: ' + user._id);
        return false;
      }
      return true;
    };
  }

  @Subscription(() => Int)
  static resendRegistrationMailsToStudentsWithoutSentRegistrationMail() {
    return (gqlFields: BaseQLSelectionSet<Int>) => {
      return from(MailEventLog.getNoMailSentEventLogs()).pipe(
        switchMap(users => this._resendRegistrationMailsTo(users.map(user => user._id))),
      );
    };
  }

  private static _resendRegistrationMailsTo(userIDs: string[]) {
    let mailsSent = 0;
    return of(...userIDs).pipe(
      concatMap(userID =>
        from(this.sendRegistrationMailForUserID(userID)().catch(() => false)).pipe(
          map(() => ++mailsSent),
          delay(100),
        ),
      ),
    );
  }

  @Query(() => Boolean)
  public static emailForAccountIsStudent(@Arg('email', () => String) email: string) {
    return async (gqlFields: void) => {
      const userCol = await getRawCollection<ChancenwerkUser>(this._collectionName);
      const user = constructValue(await userCol.findOne({ email })) as ChancenwerkUser;
      if (!user) return false;
      if (user.checkRole(Role.STUDENT)) {
        return true;
      }
      return false;
    };
  }

  @Query(() => String)
  public static getIDForEmailOrUsername(@Arg('emailOrUsername', () => String) emailOrUsername: string) {
    return async (gqlFields: void) => {
      const userCol = await getRawCollection<ChancenwerkUser>(this._collectionName);
      const user = await userCol.findOne({ $or: [{ email: emailOrUsername }, { username: emailOrUsername }] });

      // Empty string to indicate, that no user was found
      if (!user) return '';

      return user._id;
    };
  }

  @Query(() => Boolean)
  public static studentIsActivatedForCampus(@Arg('email', () => String) email: string) {
    return async (gqlFields: void) => {
      const userCol = await getRawCollection<ChancenwerkUser>(this._collectionName);
      const user = await userCol.findOne({ $or: [{ email }, { username: email }] });
      if (!user) return true;
      const userCon = constructValue(user) as ChancenwerkUser;
      if (userCon._class === 'Student') {
        const student = userCon as Student;
        if (
          student.joinInformation.campusRegisterDataSend &&
          (!student.kuendigung?.austrittsdatum || student.kuendigung?.austrittsdatum > DateTime.now())
        )
          return true;
        return false;
      }
      return true;
    };
  }

  override toString(short: boolean = false) {
    if (!this.vorname && !this.nachname) return this.email;
    if (!this.vorname && this.nachname) return this.nachname;
    if (this.vorname && !this.nachname) return !short ? this.vorname : this.vorname.split(' ')[0];
    return `${!short ? this.vorname : (this.vorname ?? '').split(' ')[0]} ${this.nachname}`;
  }

  static get toStringSelectionSet() {
    return { vorname: 1, nachname: 1, email: 1, title: 1 } as BaseQLSelectionSet<ChancenwerkUser>;
  }

  toShortString() {
    if (!this.vorname && !this.nachname) return this.email;
    if (!this.vorname && this.nachname) return this.nachname;
    if (this.vorname && !this.nachname) return this.vorname.split(' ')[0];
    return `${this.vorname?.split(' ')[0]} ${this.nachname}`;
  }

  static override async afterRemove(id: string, entityData: any): Promise<void> {
    await super.afterRemove(id, entityData);
    const relationshipCollection = await getRawCollection(SemanticRelationship._collectionName);
    await relationshipCollection.deleteMany({ $or: [{ objectID: id }, { subjectID: id }] });
  }
}

export const PERSON_BLACKLIST = {
  _class: 1,
  _id: 1,
  username: 1,
  roles: 1,
  flags: 1,
  createdBy: 1,
  createdByPerson: 1,
  createdAt: 1,
  studentRelationships: 1,
  ios_sub: 1,
  isOnline: 1,
  adresse: 1,
  lastPasswordChange: 1,
  lastActivity: 1,
  contactSource: 1,
  contactSourcePersonID: 1,
  contactSourcePerson: 1,
  personalSignOnChrstimasCardFromID: 1,
  personalSignOnChrstimasCardFromIDs: 1,
  personalSignOnChrstimasCardFrom: 1,
  savedCampusSectionIDs: 1,
  customBusinessAddressEnabled: 1,
  activated: 1,
  registeredViaToken: 1,
  customBusinessAddress: 1,
  businessAddressPartnerID: 1,
  businessAddress: { _class: 1, partnerID: 1, address: { _class: 1, zipCity: { _class: 1 } } },
  attendanceStats: { _class: 1 },
  vertraege: 1,
  unterlagen: 1,
  kontoInformationen: 1,
  onboarding: 1,
  workshopsAndTrainingInformation: 1,
  workAvailabilities: 1,
  guestAccount: 1,
  studentID: 1,
  employeeID: 1,
  buts: 1,
  schoolInformation: 1,
  kpfs: 1,
  legalGuardian1: 1,
  legalGuardian2: 1,
  notifyKickOff: 1,
  inactive: 1,
  bankInformation: 1,
  lernfoerderungDuerchfuehrungsTagChoosen: 1,
  intensiveCoursesChoosen: 1,
  funding: 1,
  legalGuardianContactMethods: 1,
  joinInformation: 1,
  kuendigung: 1,
  consent: 1,
  daysAvailable: 1,
  mitgliedschaft: 1,
  calledBackAt: 1,
  editedByInternal: 1,
  bildungsKarteNummer: 1,
  preferedFirstSubject: 1,
  preferedSecondSubject: 1,
  preferedThirdSubject: 1,
  preferedOtherSubject: 1,
  type: 1,
  partnerRelations: 1,
  activeRelations: 1,
  inactiveRelations: 1,
  kpfRelations: 1,
  levumiResults: 1,
  lernhefteWorkingOn: 1,
  lernhefteWorkingOnIDs: 1,
  lernhefteFinished: 1,
  lernhefteFinishedIDs: 1,
  intensiveCoursesSingleChoosen: 1,
  examPreperationChoosen: 1,
};
